import React, { useContext, useState } from "react";
import PropTypes from "prop-types";
import { ConfigContext } from "../Config/ConfigContext";
import { TenantContext } from "../GlobalContextProviders/TenantContext";
import EnhancedTable from "../EnhancedTable";
import {
	Box,
	Button,
	Stack,
	Typography,
	TextField,
	Avatar,
	Alert,
	Paper,
	Grid,
	Chip,
	Tooltip,
	Divider,
	FormControl,
	FormLabel,
	RadioGroup,
	FormControlLabel,
	Radio,
	FormHelperText
} from "@mui/material";
import useApi from "../hooks/useApi";

/**
 * MembersGrid displays members' information and handles inviting new members.
 *
 * UX improvements:
 * - Replace long, inline descriptive text with a compact informational Alert for readability.
 * - Use a Paper container and Grid layout to organize the invite form, improving spacing and responsiveness.
 * - Add subtle helper/caption text under the form instead of crowding the main header.
 * - Add a small "total members" indicator above the table.
 * - Use a Tooltip on the Invite button to clarify its action without cluttering the page.
 *
 * Tooltip guidance:
 * - Tooltips are best for brief, contextual hints (e.g., what the Invite button does).
 * - Longer guidance (what a workspace is, how invitations work) is better as inline help like an Alert,
 *   so users can read it without hovering and it remains accessible on touch devices.
 *
 * Invite types:
 * - You can now invite two distinct types of participants to a case:
 *   - Client (signUpType === "client")
 *   - Attorney / Legal Counsel (signUpType === "organization")
 * - The selected signUpType is sent to the API during the invite call.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Array} props.rows - An array of member objects.
 * @returns {JSX.Element} The rendered component.
 */
export default function MembersGrid({ 
  rows: initialRows,
  TenantsName = 'Workspace',
  TenantsNamePlural = TenantsName + 's'
}) {
	const { config } = useContext(ConfigContext); // Reserved for future use; intentionally retained.
	const { currentTenant, reloadTenants } = useContext(TenantContext);
	const api = useApi();

	const [inviteEmail, setInviteEmail] = useState("");
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [signUpType, setSignUpType] = useState("client"); // "client" | "organization"
	const [formErrors, setFormErrors] = useState({});
	const [errored, setErrored] = useState(false);
	const [error, setError] = useState("");

	/**
	 * Table column definitions for the Members grid.
	 */
	const headCells = [
		{
			id: "profilePicture",
			label: "Profile",
			render: ({ rowData }) => (
				<Avatar
					alt={`${rowData.firstName} ${rowData.lastName}`}
					src={rowData.profilePicture}
					sx={{ width: 50, height: 50 }}
				/>
			),
		},
		{ id: "name", label: "Name" },
		{ id: "email", label: "Email" },
		{ id: "role", label: "Role" },
		{
			id: "acceptedInvite",
			label: "Invite Status",
			render: ({ rowData }) =>
				rowData.acceptedInvite ? "Accepted" : "Pending",
		},
		{ id: "active", label: "Active" },
	];

	/**
	 * Build rows from either supplied data or the current tenant's members.
	 */
	const rows =
		initialRows?.length > 0
			? initialRows
			: currentTenant.members.map((member) => ({
					_id: member.user._id,
					profilePicture: member.user.profilePicture,
					firstName: member.user.firstName,
					lastName: member.user.lastName,
					name: `${member.user.firstName} ${member.user.lastName}`,
					email: member.user.email,
					role: member.role,
					acceptedInvite: member.acceptedInvite,
					active: member.active,
			  }));

	const memberCount = rows?.length || 0;

	/**
	 * Validates the invitation form.
	 *
	 * @returns {boolean} True if the form is valid, otherwise false.
	 */
	const validateForm = () => {
		const errors = {};
		if (!firstName.trim()) errors.firstName = "First name is required.";
		if (!lastName.trim()) errors.lastName = "Last name is required.";
		if (!inviteEmail.trim()) {
			errors.inviteEmail = "Email is required.";
		} else {
			// Light email format hinting; server remains source of truth.
			const emailLike = /\S+@\S+\.\S+/;
			if (!emailLike.test(inviteEmail.trim())) {
				errors.inviteEmail = "Please enter a valid email address.";
			}
		}
		// Ensure a valid signUpType is selected
		const allowedTypes = ["client", "organization"];
		if (!allowedTypes.includes(signUpType)) {
			errors.signUpType = "Select who you’re inviting.";
		}

		setFormErrors(errors);
		return Object.keys(errors).length === 0;
	};

	/**
	 * Handles the invitation submission.
	 *
	 * @async
	 * @returns {Promise<void>}
	 */
	const handleInvite = async () => {
		if (!validateForm()) {
			setErrored(true);
			setError("Please fill in all required fields.");
			return;
		}

		try {
			await api.tenant.invite({
				data: {
					firstName: firstName,
					lastName: lastName,
					email: inviteEmail,
					signUpType: signUpType, // Pass selected invite type to API
					tenantId: currentTenant._id
				}
			});

			await reloadTenants();

			// Reset form fields
			setFirstName("");
			setLastName("");
			setInviteEmail("");
			setSignUpType("client");
			setErrored(false);
			setError("");
		} catch (err) {
			setErrored(true);
			setError("Failed to send invite. Please try again.");
			console.error(err);
		}
	};

	return (
		<Box>
			{/* Page Header with active tenant context and brief description.
			    Use an info Alert for readability vs. long inline text. */}
			<Box mb={2}>
				<Stack direction="row" alignItems="center" spacing={1}>
					<Typography variant="h5">Members</Typography>
					<Chip
						size="small"
						variant="outlined"
						color="primary"
						label={`Active ${TenantsName}: ${currentTenant?.name || "Unknown"}`}
					/>
				</Stack>
				<Alert severity="info" sx={{ mt: 1 }}>
					Add people to your {TenantsName} to collaborate. Inviting a user sends them an email with a link to join this tenant.
				</Alert>
			</Box>

			{/* Error banner rendered as an Alert for improved visual hierarchy */}
			{errored && (
				<Box mb={2}>
					<Alert severity="error">Error! {error}</Alert>
				</Box>
			)}

			{/* Invitation Form organized within a Paper and Grid for better layout/spacing */}
			<Paper sx={{ p: 2, mb: 3 }} elevation={0} variant="outlined">
				<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
					<Typography variant="subtitle1">Invite a Member</Typography>
				</Stack>
				<Grid container spacing={2} alignItems="center">
					{/* Row 1: Name and Email */}
					<Grid item xs={12} sm={6} md={3}>
						<TextField
							fullWidth
							label="First Name"
							variant="outlined"
							value={firstName}
							onChange={(e) => setFirstName(e.target.value)}
							error={!!formErrors.firstName}
							helperText={formErrors.firstName}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<TextField
							fullWidth
							label="Last Name"
							variant="outlined"
							value={lastName}
							onChange={(e) => setLastName(e.target.value)}
							error={!!formErrors.lastName}
							helperText={formErrors.lastName}
						/>
					</Grid>
					<Grid item xs={12} md={6}>
						<TextField
							fullWidth
							label="Email"
							type="email"
							variant="outlined"
							value={inviteEmail}
							onChange={(e) => setInviteEmail(e.target.value)}
							error={!!formErrors.inviteEmail}
							helperText={formErrors.inviteEmail}
						/>
					</Grid>

					{/* Row 2: Invite Type and Action */}
					<Grid item xs={12} md={6}>
						<FormControl component="fieldset" error={!!formErrors.signUpType} fullWidth>
							<FormLabel component="legend">Invite Type</FormLabel>
							<RadioGroup
								row
								name="signUpType"
								value={signUpType}
								onChange={(e) => setSignUpType(e.target.value)}
							>
								<FormControlLabel value="client" control={<Radio />} label="Client" />
								<FormControlLabel value="organization" control={<Radio />} label="Attorney / Legal Counsel" />
							</RadioGroup>
							<FormHelperText>
								{formErrors.signUpType || "Choose whether you're inviting a client or an attorney/legal counsel."}
							</FormHelperText>
						</FormControl>
					</Grid>
					<Grid item xs={12} md={6}>
						<Tooltip title="Send an email invite to join this workspace">
							<span>
								<Button
									fullWidth
									variant="contained"
									onClick={handleInvite}
								>
									Invite
								</Button>
							</span>
						</Tooltip>
					</Grid>
				</Grid>
				<Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
					We’ll email an invite with a join link. Pending invitations will show below as “Invite Status: Pending.”
				</Typography>
			</Paper>

			{/* Members list header to separate the form and the table visually */}
			<Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1 }}>
				<Typography variant="subtitle2" color="text.secondary">
					Members in this workspace
				</Typography>
				<Typography variant="caption" color="text.secondary">
					{memberCount} total
				</Typography>
			</Stack>
			<Divider sx={{ mb: 2 }} />

			{/* Members Enhanced Table */}
			<EnhancedTable
				tableName="Members"
				headCells={headCells}
				rows={rows}
				refreshFunction={reloadTenants}
				actionRowClick={(data) => console.log("Row clicked", data)}
				actionDelete={(selectedRows) =>
					console.log("Delete rows", selectedRows)
				}
			/>
		</Box>
	);
}

MembersGrid.propTypes = {
	/**
	 * Array of member objects.
	 */
	rows: PropTypes.array,
};